import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  isTooManyEmailVerificationRequestsError,
  isChangeEmailTakenError,
  isChangeEmailWrongPassword,
} from '../../util/errors';
import * as validators from '../../util/validators';
import { verify, changeEmail, changeEmailClear } from '../../ducks/EmailVerification.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { logout } from '../../ducks/Auth.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { parse } from '../../util/urlHelpers';
import { pathByRouteName } from '../../util/routes';
import { ensureCurrentUser } from '../../util/data';
import routeConfiguration from '../../routeConfiguration';
import {
  Page,
  NamedRedirect,
  IconEmailAttention,
  InlineTextButton,
  Modal,
  FieldTextInput,
  Form,
  PrimaryButton,
} from '../../components';
import { EmailVerificationForm } from '../../forms';

import css from './EmailVerificationPage.css';

const parseVerificationToken = search => {
  const urlParams = parse(search);
  const verificationToken = urlParams.t;
  if (verificationToken) {
    return `${verificationToken}`;
  }
  return null;
};

const ChangeEmailModal = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    onChangeEmail,
    changeEmailInProgress,
    changeEmailError,
    changeEmailSuccess,
    intl,
  } = props;

  const emailRequired = validators.required(
    intl.formatMessage({ id: 'EmailVerificationPage.changeEmailRequired' })
  );
  const emailValid = validators.emailFormatValid(
    intl.formatMessage({ id: 'EmailVerificationPage.changeEmailInvalid' })
  );
  const passwordRequired = validators.required(
    intl.formatMessage({ id: 'EmailVerificationPage.changePasswordRequired' })
  );

  let errorMessage = null;
  if (changeEmailError) {
    if (isChangeEmailTakenError(changeEmailError)) {
      errorMessage = intl.formatMessage({ id: 'EmailVerificationPage.changeEmailTaken' });
    } else if (isChangeEmailWrongPassword(changeEmailError)) {
      errorMessage = intl.formatMessage({ id: 'EmailVerificationPage.changeEmailWrongPassword' });
    } else if (isTooManyEmailVerificationRequestsError(changeEmailError)) {
      errorMessage = intl.formatMessage({
        id: 'EmailVerificationPage.changeEmailTooManyRequests',
      });
    } else {
      errorMessage = intl.formatMessage({ id: 'EmailVerificationPage.changeEmailFailed' });
    }
  }

  return (
    <Modal
      id="EmailVerificationChangeEmail"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      contentClassName={css.modalContent}
    >
      <h2 className={css.modalTitle}>
        <FormattedMessage id="EmailVerificationPage.changeEmailModalTitle" />
      </h2>

      {changeEmailSuccess ? (
        <p className={css.modalSuccess}>
          <FormattedMessage id="EmailVerificationPage.changeEmailSuccess" />
        </p>
      ) : (
        <FinalForm
          onSubmit={values => onChangeEmail(values)}
          render={({ handleSubmit, invalid }) => {
            const submitDisabled = invalid || changeEmailInProgress;
            return (
              <Form onSubmit={handleSubmit}>
                <div className={css.modalField}>
                  <FieldTextInput
                    type="email"
                    id="changeEmail"
                    name="email"
                    label={intl.formatMessage({
                      id: 'EmailVerificationPage.changeEmailLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'EmailVerificationPage.changeEmailPlaceholder',
                    })}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />
                </div>
                <div className={css.modalField}>
                  <FieldTextInput
                    type="password"
                    id="changeEmailPassword"
                    name="currentPassword"
                    label={intl.formatMessage({
                      id: 'EmailVerificationPage.changePasswordLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'EmailVerificationPage.changePasswordPlaceholder',
                    })}
                    validate={passwordRequired}
                  />
                </div>

                {errorMessage ? <p className={css.modalError}>{errorMessage}</p> : null}

                <PrimaryButton
                  className={css.modalSubmitButton}
                  type="submit"
                  inProgress={changeEmailInProgress}
                  disabled={submitDisabled}
                >
                  <FormattedMessage id="EmailVerificationPage.changeEmailSubmit" />
                </PrimaryButton>
              </Form>
            );
          }}
        />
      )}
    </Modal>
  );
};

export const EmailVerificationPageComponent = props => {
  const {
    currentUser,
    intl,
    scrollingDisabled,
    submitVerification,
    emailVerificationInProgress,
    verificationError,
    location,
    history,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onLogout,
    onChangeEmail,
    onChangeEmailClear,
    changeEmailInProgress,
    changeEmailError,
    changeEmailSuccess,
    onManageDisableScrolling,
  } = props;

  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('update', { hide_default_launcher: true });
    }
  }, []);

  const title = intl.formatMessage({ id: 'EmailVerificationPage.title' });
  const verificationToken = parseVerificationToken(location ? location.search : null);

  const user = ensureCurrentUser(currentUser);
  if (user && user.attributes.emailVerified) {
    const accountRole = user.attributes?.profile?.publicData?.account_role;
    const basicInfoDone = user.attributes?.profile?.metadata?.basicInfoDone;
    if (accountRole !== 'customer' && !basicInfoDone) {
      return <NamedRedirect name="BasicInformationPage" />;
    }
    return <NamedRedirect name="LandingPage" />;
  }

  const handleLogout = () => {
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration());
      if (typeof window === 'object') {
        window.Intercom('shutdown');
        const appId = process.env.REACT_APP_INTERCOM_APP_KEY;
        window.Intercom('boot', {
          api_base: 'https://api-iam.intercom.io',
          app_id: appId,
        });
      }
      if (typeof window !== 'undefined') {
        window.location = path;
      }
    });
  };

  const handleCloseChangeEmailModal = () => {
    setIsChangeEmailModalOpen(false);
    onChangeEmailClear();
  };

  const handleChangeEmail = values => {
    onChangeEmail(values).then(() => {
      // Modal stays open showing success message; user sees updated email on card
    });
  };

  // Resend error message
  let resendErrorMessage = null;
  if (sendVerificationEmailError) {
    const resendErrorId = isTooManyEmailVerificationRequestsError(sendVerificationEmailError)
      ? 'EmailVerificationPage.resendEmailTooManyRequests'
      : 'EmailVerificationPage.resendEmailFailed';
    resendErrorMessage = (
      <p className={css.error}>
        <FormattedMessage id={resendErrorId} />
      </p>
    );
  }

  const { email, pendingEmail } = user.id ? user.attributes : {};
  const emailToShow = pendingEmail || email;

  // Token-based verification flow (user clicked email link)
  if (verificationToken) {
    const initialValues = { verificationToken };
    return (
      <Page title={title} scrollingDisabled={scrollingDisabled} referrer="origin">
        <div className={css.layoutWrapperMain}>
          <div className={css.root}>
            <div className={css.card}>
              {user.id ? (
                <EmailVerificationForm
                  initialValues={initialValues}
                  onSubmit={submitVerification}
                  currentUser={user}
                  inProgress={emailVerificationInProgress}
                  verificationError={verificationError}
                />
              ) : (
                <FormattedMessage id="EmailVerificationPage.loadingUserInformation" />
              )}
            </div>
          </div>
        </div>
      </Page>
    );
  }

  // Default: informational card (no token)
  return (
    <Page title={title} scrollingDisabled={scrollingDisabled} referrer="origin">
      <div className={css.layoutWrapperMain}>
        <div className={css.root}>
          <div className={css.card}>
            <IconEmailAttention className={css.icon} />

            <h1 className={css.title}>
              <FormattedMessage id="EmailVerificationPage.verifyEmailTitle" />
            </h1>

            <p className={css.emailInfo}>
              <FormattedMessage
                id="EmailVerificationPage.emailSent"
                values={{ email: <strong>{emailToShow}</strong> }}
              />
            </p>

            <p className={css.description}>
              <FormattedMessage id="EmailVerificationPage.description" />
            </p>

            {changeEmailSuccess ? (
              <p className={css.success}>
                <FormattedMessage id="EmailVerificationPage.changeEmailSuccess" />
              </p>
            ) : null}

            {resendErrorMessage}

            <div className={css.actions}>
              <p className={css.actionRow}>
                {sendVerificationEmailInProgress ? (
                  <FormattedMessage id="EmailVerificationPage.resendingEmail" />
                ) : (
                  <FormattedMessage
                    id="EmailVerificationPage.resendEmail"
                    values={{
                      resendLink: (
                        <InlineTextButton
                          className={css.actionLink}
                          onClick={onResendVerificationEmail}
                        >
                          <FormattedMessage id="EmailVerificationPage.resendEmailLink" />
                        </InlineTextButton>
                      ),
                    }}
                  />
                )}
              </p>
              <p className={css.actionRow}>
                <FormattedMessage
                  id="EmailVerificationPage.fixEmail"
                  values={{
                    fixLink: (
                      <InlineTextButton
                        className={css.actionLink}
                        onClick={() => setIsChangeEmailModalOpen(true)}
                      >
                        <FormattedMessage id="EmailVerificationPage.fixEmailLink" />
                      </InlineTextButton>
                    ),
                  }}
                />
              </p>
            </div>

            <div className={css.logoutWrapper}>
              <InlineTextButton className={css.logoutButton} onClick={handleLogout}>
                <FormattedMessage id="EmailVerificationPage.logout" />
              </InlineTextButton>
            </div>
          </div>

          <ChangeEmailModal
            isOpen={isChangeEmailModalOpen}
            onClose={handleCloseChangeEmailModal}
            onManageDisableScrolling={onManageDisableScrolling}
            onChangeEmail={handleChangeEmail}
            changeEmailInProgress={changeEmailInProgress}
            changeEmailError={changeEmailError}
            changeEmailSuccess={changeEmailSuccess}
            intl={intl}
          />
        </div>
      </div>
    </Page>
  );
};

EmailVerificationPageComponent.defaultProps = {
  currentUser: null,
  verificationError: null,
  sendVerificationEmailError: null,
  changeEmailError: null,
};

const { bool, func, object, shape, string } = PropTypes;

EmailVerificationPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  scrollingDisabled: bool.isRequired,
  submitVerification: func.isRequired,
  emailVerificationInProgress: bool.isRequired,
  verificationError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onLogout: func.isRequired,
  onChangeEmail: func.isRequired,
  onChangeEmailClear: func.isRequired,
  changeEmailInProgress: bool.isRequired,
  changeEmailError: propTypes.error,
  changeEmailSuccess: bool.isRequired,
  onManageDisableScrolling: func.isRequired,

  // from withRouter
  location: shape({
    search: string,
  }).isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  const {
    verificationError,
    verificationInProgress,
    changeEmailInProgress,
    changeEmailError,
    changeEmailSuccess,
  } = state.EmailVerification;
  return {
    verificationError,
    emailVerificationInProgress: verificationInProgress,
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    changeEmailInProgress,
    changeEmailError,
    changeEmailSuccess,
  };
};

const mapDispatchToProps = dispatch => ({
  submitVerification: ({ verificationToken }) => dispatch(verify(verificationToken)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onLogout: () => dispatch(logout()),
  onChangeEmail: params => dispatch(changeEmail(params)),
  onChangeEmailClear: () => dispatch(changeEmailClear()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const EmailVerificationPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(EmailVerificationPageComponent);

EmailVerificationPage.loadData = (params, search) => {
  const token = parseVerificationToken(search);
  return verify(token);
};

export default EmailVerificationPage;
